import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppToast } from './useAppToast'

/**
 * Quanto tempo o botão fica no estado "copiado" antes de voltar sozinho ao ícone normal.
 *
 * Dois segundos, que é o que as três telas que tinham esse estado já usavam
 * (`ContactSnippetPage`, `PostsFormModal` e o `hasCopied` do Chakra no `HeroLinkButton`).
 */
export const COPIED_RESET_MS = 2000

export interface ICopyOptions {
  /**
   * O que foi copiado, minúsculo e no singular: `link`, `código`, `chave Pix`. A frase do toast
   * é montada a partir dele — a mesma convenção do `ConfirmDialog` e do `TableEmptyRow`.
   */
  entity?: string
  /** Concordância pt-BR: ligue para substantivo feminino (`URL copiada`). */
  feminine?: boolean
  /**
   * Descrição do toast, nos dois desfechos. Passe o próprio valor quando ele for curto e
   * legível (uma URL): na falha é por onde quem clicou copia na mão.
   */
  description?: string
}

export interface ICopyToClipboard {
  /** Liga o ícone de check logo depois da cópia; volta a `false` sozinho. */
  copied: boolean
  /** Escreve na área de transferência e notifica. Devolve se a escrita aconteceu. */
  copy: (value: string, options?: ICopyOptions) => Promise<boolean>
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * Cópia para a área de transferência — a guarda, o `await`, o toast e o estado "copiado".
 *
 * Eram 15 chamadas em 11 arquivos e seis variações: sem `await` nem `catch` (8 delas), com o
 * toast **antes** da escrita (`FinanceUrlRedirectorsCreateModal`), com `.then` + um
 * `setTimeout(2000)` local, com guarda de suporte e estado próprio, e com `try/catch` sem
 * guarda. Só o `HeroLinkButton` usava o `useClipboard` do Chakra.
 *
 * **O defeito que isto fecha:** fora de contexto seguro `navigator.clipboard` é `undefined` (e a
 * chamada estoura um `TypeError`), e com permissão negada a Promise rejeita — em 9 das 15
 * chamadas o toast de sucesso aparecia do mesmo jeito, e nada era copiado. O
 * `SurveyReportPage` documentava exatamente esse defeito num comentário ao lado da correção
 * dele, que nunca foi propagada; agora ela mora aqui.
 *
 * O gatilho normal é o `<CopyButton>`, que já traz o hook dentro. Use o hook direto onde o
 * gatilho não é um botão de ação — um `ActionFab`, um ícone dentro de uma aba, o botão do
 * banner escuro do `DetailHero` — a mesma divisão que o `useFilePicker` faz com o `<FileInput>`.
 */
export function useCopyToClipboard(): ICopyToClipboard {
  const toast = useAppToast()
  const [copied, setCopied] = useState(false)
  const resetTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(resetTimeout.current), [])

  const copy = useCallback(
    async (value: string, { entity, feminine = false, description }: ICopyOptions = {}) => {
      try {
        // Sem contexto seguro a API nem existe, então a chamada direta estouraria um TypeError.
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard API indisponível')

        await navigator.clipboard.writeText(value)

        toast.success(
          entity ? `${capitalize(entity)} copiad${feminine ? 'a' : 'o'}` : 'Copiado para a área de transferência',
          description
        )

        setCopied(true)
        clearTimeout(resetTimeout.current)
        resetTimeout.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)

        return true
      } catch {
        toast.error(`Não foi possível copiar${entity ? ` ${feminine ? 'a' : 'o'} ${entity}` : ''}`, description)

        return false
      }
    },
    [toast]
  )

  return { copied, copy }
}
